import { useState } from "react";
import { LifeBuoy, X, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Support() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setSubject("");
    setMessage("");
    setSent(false);
    setErr("");
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const s = subject.trim();
    const m = message.trim();
    if (!s || !m) {
      setErr("Please add a subject and a message.");
      return;
    }
    setSending(true);
    setErr("");
    try {
      await api.post("/support", { subject: s, message: m });
      setSent(true);
      setSubject("");
      setMessage("");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Could not send your message.");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="support-launcher"
        className="fixed bottom-20 right-6 z-40 flex items-center gap-2 pl-4 pr-5 py-3 bg-black text-white rounded-full shadow-lg hover:bg-neutral-800 transition-colors"
        style={{ boxShadow: "0 8px 24px rgba(10,10,10,0.18)" }}
      >
        <LifeBuoy size={16} />
        <span className="text-[13px] font-semibold">Support</span>
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-20 right-6 z-40 w-[380px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-96px)] flex flex-col card overflow-hidden fade-in"
      style={{ boxShadow: "0 12px 36px rgba(10,10,10,0.22)" }}
      data-testid="support-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-black text-white flex items-center justify-center">
            <LifeBuoy size={13} />
          </div>
          <div>
            <div className="font-display font-bold text-[14px] tracking-tight leading-none">
              Support
            </div>
            <div className="text-[10.5px] text-neutral-500 mt-0.5">
              We'll reply to {user?.email || "your account email"}
            </div>
          </div>
        </div>
        <button onClick={close} className="text-neutral-400 hover:text-black" aria-label="Close">
          <X size={16} />
        </button>
      </div>

      {sent ? (
        <div className="p-5">
          <div className="text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-3 mb-4">
            Thanks — we've got it. You'll hear back at <strong>{user?.email}</strong>.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={reset}
              className="btn text-[12px] py-1.5 px-3"
              data-testid="support-send-another"
            >
              Send another
            </button>
            <button
              type="button"
              onClick={close}
              className="btn-primary text-[12px] py-1.5 px-3"
              data-testid="support-done"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="p-4 space-y-3">
          <div>
            <label htmlFor="support-subject" className="block text-[11.5px] font-semibold text-neutral-600 mb-1">
              Subject
            </label>
            <input
              id="support-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              disabled={sending}
              placeholder="What's going on?"
              className="input w-full text-[13px]"
              data-testid="support-subject"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="support-message" className="block text-[11.5px] font-semibold text-neutral-600 mb-1">
              Message
            </label>
            <textarea
              id="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={5000}
              disabled={sending}
              rows={6}
              placeholder="Tell us what happened — include any details that'd help us reproduce or respond."
              className="input w-full text-[13px] resize-y"
              data-testid="support-message"
            />
          </div>
          {err && (
            <div
              className="px-3 py-2 text-[12px] rounded-[10px] border"
              style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}
            >
              {err}
            </div>
          )}
          <div className="flex items-center justify-end">
            <button
              type="submit"
              disabled={sending || !subject.trim() || !message.trim()}
              className="btn btn-primary text-[12px] px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="support-submit"
            >
              {sending ? "Sending…" : (<><Send size={12} /> Send</>)}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
